import type { Unit } from './getElapsedTime';

/**
 * Formats a time unit value into a readable string based on the provided unit and translation function.
 *
 * @param {string | number} value - The numeric or string representation of the time value.
 * @param {Unit} unit - The unit of time (e.g., "second", "minute", etc.) derived from dayjs `diff` method.
 * @param {(val: string) => string} t - A translation function that returns a localized string for a given time unit.
 *
 * @example
 * formatTimeUnit(2, 'minute', (val) => val) // "2 minutes"
 * formatTimeUnit(1, 'hour', (val) => val) // "1 hour"
 * formatTimeUnit(0.5, 'day', (val) => val) // "< 1 day"
 */
const formatTimeUnit = (
  value: string | number,
  unit: Unit,
  t: (val: string) => string
) => {
  if (value === null || isNaN(Number(value))) return '';

  value = Number(value);

  if (value < 1) {
    return `< 1 ${t(unit)}`;
  } else if (value === 1) {
    return `1 ${t(unit)}`;
  } else if (value > 1) {
    // eslint-disable-next-line sonarjs/no-nested-template-literals
    return `${value} ${t(`${unit}s`)}`;
  }
};

export default formatTimeUnit;
